// usePageFeedback — the per-page thumbs up/down (+ optional comment) flow shared
// by the unified subheader and the article header. It owns the vote state, the
// initial read, the comment box (opened by either vote), and the transient
// "thanks" acknowledgement, and persists everything through pageFeedbackService.
//
// Analytics are injected (feedbackClick/commentSend/commentSkip) so each surface
// can report through its own pipeline while the flow itself stays identical.
import { useCallback, useEffect, useRef, useState } from "react";

import {
  getPageFeedback,
  sentimentToVote,
  submitPageFeedback,
  voteToSentiment,
} from "../../services/pageFeedbackService";

// Comment box copy per vote. Comments are capped at COMMENT_MAX_LENGTH, which
// matches the es-engage-api pageFeedback limit.
export const COMMENT_PROMPTS = {
  up: {
    title: "What was helpful?",
    placeholder: "Tell us what helped, or anything we could still improve…",
  },
  down: {
    title: "What could be better?",
    placeholder: "Tell us what we could improve…",
  },
};
export const COMMENT_MAX_LENGTH = 2000;

export const usePageFeedback = ({ url, enabled = true, analytics } = {}) => {
  const [vote, setVote] = useState(null);
  // Neutral until the initial read resolves so the thumbs don't flash a vote.
  const [ready, setReady] = useState(!enabled);
  const [comment, setComment] = useState("");
  const [commentOpen, setCommentOpen] = useState(false);
  const [thanks, setThanks] = useState(false);

  // Latest analytics callbacks without re-creating the handlers every render.
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  // Single tracked timer so re-showing "thanks" (e.g. like → unlike → like)
  // cancels the previous countdown instead of letting an orphaned timer from an
  // earlier vote hide the toast early.
  const thanksTimer = useRef(null);
  const showThanks = useCallback(() => {
    setThanks(true);
    clearTimeout(thanksTimer.current);
    thanksTimer.current = setTimeout(() => setThanks(false), 2000);
  }, []);
  const hideThanks = useCallback(() => {
    clearTimeout(thanksTimer.current);
    setThanks(false);
  }, []);
  useEffect(() => () => clearTimeout(thanksTimer.current), []);

  // Track the current url so a save that resolves after navigation doesn't roll
  // back / mutate the vote state of a different page (the hook is persistent).
  const activeUrl = useRef(url);
  useEffect(() => {
    activeUrl.current = url;
  }, [url]);
  // Bumped on every vote save, so only the latest save may roll back; an older
  // save that fails late must not undo a newer vote.
  const saveSeq = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setReady(false);
    getPageFeedback(url).then((record) => {
      if (cancelled) return;
      setVote(sentimentToVote(record?.sentiment));
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  // Cast (or, on re-tap, clear) a vote. Returns the resolved vote so callers can
  // fire the matching celebration animation.
  const castVote = useCallback(
    (nextVote) => {
      const previous = vote;
      const resolved = vote === nextVote ? null : nextVote;
      setVote(resolved);
      // Persist, and roll back the optimistic vote if the save fails (the service
      // resolves null on HTTP/network error) so a failed vote doesn't look saved.
      const seq = ++saveSeq.current;
      submitPageFeedback({ url, sentiment: voteToSentiment(resolved) }).then(
        (res) => {
          if (
            res === null &&
            seq === saveSeq.current &&
            activeUrl.current === url
          ) {
            setVote(previous);
            setCommentOpen(false);
            hideThanks();
          }
        },
      );
      if (resolved) analyticsRef.current?.feedbackClick?.(resolved);

      // Either vote offers an optional comment; clearing the vote closes it.
      // Every vote and comment is its own row, so this never loses a comment.
      hideThanks();
      setComment("");
      setCommentOpen(Boolean(resolved));
      return resolved;
    },
    [url, vote, hideThanks],
  );

  const skipComment = useCallback(() => {
    analyticsRef.current?.feedbackCommentSkip?.();
    setCommentOpen(false);
  }, []);

  // The comment is saved with the vote it was written under. Sending a blank
  // box is a skip: every save adds a row, so it would only log the vote again.
  const sendComment = useCallback(() => {
    if (!comment.trim()) {
      skipComment();
      return;
    }
    analyticsRef.current?.feedbackCommentSend?.();
    submitPageFeedback({ url, sentiment: voteToSentiment(vote), comment });
    setCommentOpen(false);
    showThanks();
  }, [url, vote, comment, showThanks, skipComment]);

  return {
    vote,
    ready,
    comment,
    setComment,
    commentOpen,
    setCommentOpen,
    thanks,
    prompt: COMMENT_PROMPTS[vote] ?? COMMENT_PROMPTS.down,
    castVote,
    sendComment,
    skipComment,
  };
};
